<template>
    <b-row :class="{ 'my-3 border note': true, 'pinned': note.pinned, 'child': isChild, 'deleted': isDeleted }">
        <b-col>
            <div v-if="!isEditing" class="note-body d-flex flex-column">
                <div v-if="note.activityType === 0 || note.activityType">
                    <b-badge v-if="note.activityType === NoteActivityType.PhoneCall" variant="primary" class="mb-0">
                        <i class="fa fa-phone mr-1"></i>
                        Phone Call
                    </b-badge>
                    <b-badge v-if="note.activityType === NoteActivityType.TextMessage" variant="primary" class="mb-0">
                        <i class="fa fa-comment-dots mr-1"></i>
                        Text Message
                    </b-badge>
                    <b-badge v-if="note.activityType === NoteActivityType.Electronic" variant="primary" class="mb-0">
                        <i class="fa fa-at mr-1"></i>
                        Electronic
                    </b-badge>
                    <b-badge v-if="note.activityType === NoteActivityType.Portal" variant="primary" class="mb-0">
                        <i class="fa fa-wifi mr-1"></i>
                        Portal
                    </b-badge>
                    <b-badge v-if="note.activityType === NoteActivityType.Fax" variant="primary" class="mb-0">
                        <i class="fa fa-fax mr-1"></i>
                        Fax
                    </b-badge>
                </div>
                <div v-if="note.body?.trim()">{{ note.body }}</div>
                <div v-if="note.updated" class="text-muted small">
                    <i class="fas fa-edit"></i> Edited
                </div>
            </div>

            <div v-else class="note-body d-flex flex-column">
                <div v-if="!isChild" class="mb-1">
                    <b-dropdown size="sm" :text="activityTypeLabel" variant="primary">
                        <b-dropdown-item @click="selectActivityType(null)">
                            <i class="fa fa-times mr-1"></i> None
                        </b-dropdown-item>
                        <b-dropdown-item @click="selectActivityType(NoteActivityType.PhoneCall)">
                            <i class="fa fa-phone mr-1"></i> Phone Call
                        </b-dropdown-item>
                        <b-dropdown-item @click="selectActivityType(NoteActivityType.TextMessage)">
                            <i class="fa fa-comment-dots mr-1"></i> Text Message
                        </b-dropdown-item>
                        <b-dropdown-item @click="selectActivityType(NoteActivityType.Electronic)">
                            <i class="fa fa-at mr-1"></i> Electronic
                        </b-dropdown-item>
                        <b-dropdown-item @click="selectActivityType(NoteActivityType.Portal)">
                            <i class="fa fa-wifi mr-1"></i> Portal
                        </b-dropdown-item>
                        <b-dropdown-item @click="selectActivityType(NoteActivityType.Fax)">
                            <i class="fa fa-fax mr-1"></i> Fax
                        </b-dropdown-item>
                    </b-dropdown>
                </div>
                <b-form-textarea ref="editTextarea" v-model="editBody" rows="4" class="mb-0"></b-form-textarea>
                <div>
                    <b-button size="sm" variant="primary" class="mb-0" @click="saveEdit" :disabled="isSaving">
                        <i class="fas fa-check"></i> Save
                    </b-button>
                    <b-button size="sm" variant="secondary" class="ml-1 mb-0" @click="cancelEdit" :disabled="isSaving">
                        <i class="fas fa-times"></i> Cancel
                    </b-button>
                </div>
            </div>

            <div class="byline-bar d-flex align-items-center">
                <div class="flex-fill cursor-pointer" @click="$emit('toggle-verbose-byline')">
                    <span :title="verboseByline ? undefined : displayUser" v-if="note.updated">
                        {{ verboseByline ? displayUser : formatName(displayUser) }}
                    </span>
                    <span :title="verboseByline ? undefined : note.createdBy" v-else>
                        {{ verboseByline ? note.createdBy : formatName(note.createdBy) }}
                    </span>
                    <span v-if="!verboseByline">&middot;</span>
                    <br v-if="verboseByline" />
                    <span :title="getBylineTooltip()">
                        {{ getDisplayDate() }}
                    </span>
                </div>
                <div>
                    <a v-if="canViewHistory && note.updated" href="#" @click.prevent="$emit('view-history')">
                        <i class="fas fa-history"></i> History
                    </a>
                    <a v-if="canEdit && !isEditing && !isDeleted" href="#" @click.prevent="startEdit">
                        <i class="fas fa-edit"></i> Edit
                    </a>
                    <a v-if="canDelete && !isEditing && !isDeleted" href="#" @click.prevent="deleteNote">
                        <i class="fas fa-trash"></i> Delete
                    </a>
                    <a v-if="!isChild && canPin && !isDeleted" href="#" @click.prevent="$emit('toggle-pin')">
                        <i class="fas fa-thumbtack"></i>
                        {{ note.pinned ? "Unpin" : "Pin" }}
                    </a>
                    <a v-if="!isChild && canReply && !isDeleted" href="#" @click.prevent="$emit('reply')">
                        <i class="fas fa-reply"></i> Reply
                    </a>
                    <span v-if="isDeleted" class="text-danger" :title="getDeletedTooltip()">
                        <i class="fa fa-trash mr-1"></i>
                        DELETED
                    </span>
                </div>
            </div>
        </b-col>
    </b-row>
</template>

<script lang="ts">

import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator';
import { Note, NoteActivityType } from '@/models/Note';
import moment from "moment";
import axios from 'axios';
import { NotificationOptions } from "@/util/NotificationOptionsPresets";

@Component({
    name: "NotesListItem",
    components: {}
})
export default class NotesListItem extends Vue {

    @Prop() private note!: Note;
    @Prop() private verboseByline!: boolean;
    @Prop({ default: true }) private canReply!: boolean;
    @Prop({ default: true }) private canPin!: boolean;

    private isEditing = false;
    private editBody = "";
    private editActivityType: NoteActivityType | null = null;
    private isSaving = false;

    get isChild() {
        return !!this.note.parentNoteId;
    }

    get canEdit() {
        const isOwner = this.note.createdBy === this.$user.userName;
        const hasPermission = this.$permissions.some(
            p => p.permissionCodeName === "Notes/Edit Notes"
        );
        return isOwner || hasPermission;
    }

    get canDelete() {
        const isOwner = this.note.createdBy === this.$user.userName;
        const hasPermission = this.$permissions.some(
            p => p.permissionCodeName === "Notes/Delete Notes"
        );
        return isOwner || hasPermission;
    }

    get canViewHistory() {
        return true; // All users can view history
    }

    get isDeleted() {
        return !!this.note.deleted;
    }

    get displayUser() {
        return this.note.updatedBy || this.note.createdBy;
    }

    get activityTypeLabel() {
        switch (this.editActivityType) {
            case NoteActivityType.PhoneCall:
                return "Phone Call";
            case NoteActivityType.TextMessage:
                return "Text Message";
            case NoteActivityType.Electronic:
                return "Electronic";
            case NoteActivityType.Portal:
                return "Portal";
            case NoteActivityType.Fax:
                return "Fax";
            case null:
            default:
                return "No Activity";
        }
    }

    formatName(name: string) {
        if (name.includes("/")) return name.split("/")[1];
        if (name.includes("\\")) return name.split("\\")[1];
        return name.split("@")[0];
    }

    formatDate(date: Date) {
        return moment.utc(date).local().format("LLL");
    }

    relativeDate(date: Date) {
        return moment.utc(date).fromNow();
    }

    getDisplayDate() {
        const date = this.note.updated || this.note.created;
        return this.verboseByline ? this.formatDate(date) : this.relativeDate(date);
    }

    getDeletedTooltip() {
        if (!this.note.deleted || !this.note.deletedBy) return '';
        return `Deleted by ${this.note.deletedBy} on ${this.formatDate(this.note.deleted)}`;
    }

    getBylineTooltip() {
        if (!this.note.updated) {
            return this.verboseByline ? this.relativeDate(this.note.created) : this.formatDate(this.note.created);
        }

        // Show original creator info in tooltip
        const createdInfo = `Created by ${this.formatName(this.note.createdBy)} ${this.relativeDate(this.note.created)}`;
        if (this.verboseByline) {
            return createdInfo;
        }
        return `${this.formatDate(this.note.updated || this.note.created)}\n${createdInfo}`;
    }

    startEdit() {
        this.isEditing = true;
        this.editBody = this.note.body;
        this.editActivityType = this.note.activityType ?? null;
        this.$nextTick(() => {
            (this.$refs.editTextarea as any)?.$el?.focus();
        });
    }

    cancelEdit() {
        this.isEditing = false;
        this.editBody = "";
        this.editActivityType = null;
    }

    selectActivityType(type: NoteActivityType | null) {
        this.editActivityType = type;
    }

    async saveEdit() {
        if (!this.editBody.trim()) {
            this.$notification(NotificationOptions.error("Note body cannot be empty"));
            return;
        }

        // No-op detection: if nothing changed, just cancel edit mode
        if (this.editBody.trim() === this.note.body?.trim() &&
            this.editActivityType === this.note.activityType) {
            this.cancelEdit();
            return;
        }

        this.isSaving = true;
        try {
            const response = await axios.put(`/Note/${this.note.id}`, {
                body: this.editBody,
                activityType: this.editActivityType
            });

            // Update the local note with the response data
            Object.assign(this.note, response.data);

            this.$notification(NotificationOptions.success("Note updated successfully"));
            this.isEditing = false;
            this.$emit('updated');
        } catch (error) {
            console.error('Error updating note:', error);
            this.$notification(NotificationOptions.error("Failed to update note"));
        } finally {
            this.isSaving = false;
        }
    }

    async deleteNote() {
        const confirmed = await this.$bvModal.msgBoxConfirm(
            'Are you sure you want to delete this note?',
            {
                title: 'Confirm Delete',
                size: 'sm',
                buttonSize: 'sm',
                okVariant: 'danger',
                okTitle: 'Delete',
                cancelTitle: 'Cancel',
                footerClass: 'p-2',
                hideHeaderClose: false,
                centered: true
            }
        );

        if (!confirmed) return;

        try {
            await axios.delete(`/Note/${this.note.id}`);
            this.$notification(NotificationOptions.success("Note deleted successfully"));
            this.$emit('deleted');
        } catch (error) {
            console.error('Error deleting note:', error);
            this.$notification(NotificationOptions.error("Failed to delete note"));
        }
    }

    NoteActivityType = NoteActivityType;

}

</script>

<style scoped lang="scss">
.pinned {
    background: rgba(gold, .1);
    transition: all .2s;

    .byline-bar {
        background: rgba(white, .1);
    }
}

.deleted {
    border-style: dashed !important;
    opacity: 0.8;
}

.note {
    border-radius: 0 15px 15px 15px;
    margin-bottom: 10px;
}

.child {
    border-radius: 15px 15px 0 15px;
    margin-top: 7px !important;
    margin-bottom: 5px !important;
}

.note-body {
    white-space: pre-wrap;
    line-height: 1.35;
    margin-top: 8px;
    gap: 6px;

    textarea {
        background-color: white !important;
    }
}

.byline-bar {
    font-weight: 600;
    font-size: .75em;
    border-top: 1px solid rgba(black, 0.05);
    margin-left: -15px;
    margin-right: -15px;
    padding: 3px 5px;
    margin-top: 10px;
    padding-left: 15px;
    letter-spacing: .25px;

    a {
        padding: 5px;
    }
}
</style>
